import { useLazyQuery } from '@apollo/client';
import { Container } from 'components/box';
import categoryPageQuery from 'queries/categoryPage.graphql';
import { useEffect, useRef } from 'react';
import { QueryCategoryPageOutput, QueryCategoryPageArgs } from 'types/types';
import { useCustomerContext, useStaffPreview } from 'hooks/customer';
import { useErrorNotificationHandler } from 'hooks/handle-errors';
import { setGTMPageName } from '../../util/tag-manager';
import { fetchGraphQl, parseQueryAST, isAbortError } from '../../util/graphql';
import Loading from 'components/loading';
import QueryError from 'components/query-error';
import { CategoryPage } from 'components/_pages/category/types';
import {
  categoryIdAsTitleCase,
  categoryPageProps,
} from 'components/_pages/category/utils';
import Body from 'components/_pages/category/body';
import { PageProps } from 'types/types';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { AlgoliaIndexEnum } from 'types/interface';
import { logError } from '../../util/error-logger';
import asTitleCase from '../../util/as-title-case';
import { canonicalUrl } from '../../util/url';
import config from '../../util/load-config';
import { prepMetaTags } from '../../util/seo';
import { cacheControl } from 'constants/cache-control';
import { logInvalidJSON } from '../../util/log-invalid-json';

interface CategoryPageProps {
  categoryId?: string;
  categoryPage?: CategoryPage;
}

/**
 * Takes the SSR props and passes them down to the body.
 * But on client will also check if it needs to load the staff preview;
 * and if so it will pass on the preview props instead.
 * Only down here coz it needs the `categoryPageProps` function.
 */
const BodyWrapper = ({
  categoryId,
  categoryPage: categoryPageSSR,
}: CategoryPageProps) => {
  const context = useCustomerContext();
  const mountErrors = useErrorNotificationHandler();
  const gtmPageNameRef = useRef(true);

  const [previewCategoryPage, { loading, error, data }] = useLazyQuery<
    QueryCategoryPageOutput,
    QueryCategoryPageArgs
  >(categoryPageQuery, {
    context,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  useStaffPreview(
    ({ date }) =>
      categoryId && previewCategoryPage({ variables: { categoryId, date } })
  );

  useEffect(() => {
    error && mountErrors([new Error('Preview failed')]);
  }, [error, mountErrors]);

  useEffect(() => {
    if (gtmPageNameRef.current && categoryPageSSR?.category?.name) {
      setGTMPageName(asTitleCase(categoryPageSSR.category.name));
      gtmPageNameRef.current = false;
    }
  }, [categoryPageSSR]);

  if (!categoryId) {
    return (
      <Container py={[3, null, 3]}>
        <QueryError />
      </Container>
    );
  }

  const category = data?.categories?.find(({ id }) => id === categoryId);

  const categoryPage =
    data && data.products && data.products.length > 0 && category
      ? categoryPageProps(data, categoryId, category)
      : categoryPageSSR;

  if (!categoryPage) {
    return (
      <Container py={[3, null, 3]}>
        <QueryError />
      </Container>
    );
  }

  return (
    <Loading iconTop={200} isLoading={loading} loadingText="Preview loading...">
      <Body
        {...categoryPage}
        isLazy={!data || !data.products || data.products.length === 0}
      />
    </Loading>
  );
};

export async function getServerSideProps({
  query: { id },
  res,
  resolvedUrl,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<CategoryPageProps & PageProps>
> {
  const canonical = canonicalUrl(resolvedUrl);

  const categoryId = typeof id === 'string' ? id : '';

  try {
    if (!categoryId) {
      res.statusCode = 404;
      res.setHeader('cache-control', cacheControl.temporary);

      // return unknown category props
      return { notFound: true, props: {} };
    }

    const [query, operationName] = await parseQueryAST(
      categoryPageQuery,
      'categoryPage'
    );

    const { data, errors } = await fetchGraphQl<QueryCategoryPageOutput>({
      operationName,
      query,
      variables: { categoryId },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.products) {
      // we know the category should technically always have products
      // so we can assume that this is temporary
      res.statusCode = 503;
      res.setHeader('cache-control', cacheControl.temporary);

      // log error
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(firstError.message, `categoryPage.query.${categoryId}`);
      } else {
        logError(
          `No data returned for category page: "${categoryId}"`,
          `categoryPage.query.${categoryId}`
        );
      }

      // return empty props so we can render a failure page
      return { props: { categoryId } };
    }

    const category = data.categories.find(({ id }) => id === categoryId);
    if (data.products.length > 0 && !category) {
      logError(
        `Couldn't find category data for category page: "${categoryId}"`,
        `categoryPage.query.${categoryId}.categories.find`
      );
    }

    // go ahead
    res.setHeader('cache-control', cacheControl.dynamic);

    const meta = config.seo.meta.pages[`category/${categoryId}`];

    const categoryTitle = category
      ? asTitleCase(category.name)
      : categoryIdAsTitleCase(categoryId);

    return {
      props: {
        categoryId,
        categoryPage: categoryPageProps(data, categoryId, category),
        meta: {
          messages: data.messages || null,
          isAnotherDayOnlyActive: data.configs?.anotherDayOnly?.isActive,
        },
        layout: {
          searchIndexName: AlgoliaIndexEnum.Homepage,
          selectedTopTab: 'home',
          categories: data.categories,
          hasScrollButton: true,
          hasAppDownloadWidget: true,
          moreProductsFooter: { enabled: true },
          seo: {
            ...(category?.description
              ? { description: category.description }
              : {}),
            header: {
              title: categoryTitle,
              breadcrumbs: [
                { label: "Today's Deals", url: '/' },
                { label: categoryTitle },
              ],
            },
          },
          ...(meta
            ? {
                metaTags: {
                  ...prepMetaTags(meta),
                  ...(canonical ? { canonical } : {}),
                },
              }
            : { ...(canonical ? { canonical } : {}) }),
        },
      },
    };
  } catch (e) {
    res.statusCode = isAbortError(e) ? 503 : 500;
    res.setHeader('cache-control', cacheControl.temporary);

    logError(e, 'categoryPage.catch');

    // return empty props so we can render a failure page
    return { props: {} };
  }
}

export default BodyWrapper;
